/**
 * Agent Cache Manager with TTL and Invalidation
 * 
 * Provides persistent caching for agent results with:
 * - Time-based expiration (TTL)
 * - Content-based invalidation
 * - LRU eviction policy
 * - Compression support
 * - Cache statistics
 */

import { createClient } from '@blinkdotnew/sdk';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

/**
 * Cache entry with metadata
 */
export interface CacheEntry {
  id: string;
  key: string;
  value: any;
  createdAt: number;
  expiresAt: number;
  hits: number;
  size: number; // bytes
  tags: string[];
}

/**
 * Cache statistics
 */
export interface CacheStats {
  totalEntries: number;
  totalSize: number;
  hitRate: number;
  missRate: number;
  evictions: number;
}

/**
 * Agent Cache Manager
 */
export class AgentCacheManager {
  private cache = new Map<string, CacheEntry>();
  private maxSize = 50 * 1024 * 1024; // 50MB max cache size
  private stats = {
    hits: 0,
    misses: 0,
    evictions: 0
  };

  /**
   * Get cached value if valid
   */
  async get(key: string): Promise<any | null> {
    const entry = this.cache.get(key);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check expiration
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    // Update stats
    entry.hits++;
    this.stats.hits++;

    console.log(`✅ Cache HIT: ${key} (${entry.hits} hits)`);
    return entry.value;
  }

  /**
   * Set cache value with TTL
   */
  async set(
    key: string,
    value: any,
    ttlMs: number = 300000, // 5 minutes default
    tags: string[] = []
  ): Promise<void> {
    const serialized = JSON.stringify(value);
    const size = new Blob([serialized]).size;

    // Evict if needed
    await this.evictIfNeeded(size);

    const entry: CacheEntry = {
      id: `cache_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      key,
      value,
      createdAt: Date.now(),
      expiresAt: Date.now() + ttlMs,
      hits: 0,
      size,
      tags
    };

    this.cache.set(key, entry);
    console.log(`💾 Cache SET: ${key} (${(size / 1024).toFixed(2)} KB, TTL: ${ttlMs}ms)`);

    // Persist to database for cross-session caching
    try {
      await blink.db.searchIndex.create({
        id: entry.id,
        userId: 'system',
        itemType: 'agent_cache',
        itemId: key,
        title: key,
        content: serialized,
        metadata: JSON.stringify({
          tags,
          expiresAt: entry.expiresAt,
          size
        }),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.warn('Failed to persist cache to DB:', error);
    }
  }

  /**
   * Invalidate cache by key pattern
   */
  async invalidate(pattern: string): Promise<number> {
    let count = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (this.matchesPattern(key, pattern)) {
        this.cache.delete(key);
        count++;

        // Remove from database
        try {
          await blink.db.searchIndex.delete(entry.id);
        } catch (error) {
          console.warn('Failed to delete cache from DB:', error);
        }
      }
    }

    console.log(`🗑️  Cache INVALIDATE: Pattern "${pattern}" cleared ${count} entries`);
    return count;
  }

  /**
   * Invalidate cache by tags
   */
  async invalidateByTags(tags: string[]): Promise<number> {
    let count = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (tags.some(tag => entry.tags.includes(tag))) {
        this.cache.delete(key);
        count++;

        try {
          await blink.db.searchIndex.delete(entry.id);
        } catch (error) {
          console.warn('Failed to delete cache from DB:', error);
        }
      }
    }

    console.log(`🗑️  Cache INVALIDATE: Tags [${tags.join(', ')}] cleared ${count} entries`);
    return count;
  }

  /**
   * Clear entire cache
   */
  async clear(): Promise<void> {
    const count = this.cache.size;
    this.cache.clear();

    // Clear from database
    try {
      const entries = await blink.db.searchIndex.list({
        where: { itemType: 'agent_cache', userId: 'system' }
      });
      
      for (const entry of entries) {
        await blink.db.searchIndex.delete(entry.id);
      }
    } catch (error) {
      console.warn('Failed to clear cache from DB:', error);
    }

    console.log(`🗑️  Cache CLEARED: ${count} entries removed`);
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const totalSize = Array.from(this.cache.values()).reduce(
      (sum, entry) => sum + entry.size,
      0
    );

    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;

    return {
      totalEntries: this.cache.size,
      totalSize,
      hitRate,
      missRate: 1 - hitRate,
      evictions: this.stats.evictions
    };
  }

  /**
   * Evict entries using LRU policy
   */
  private async evictIfNeeded(requiredSize: number): Promise<void> {
    const currentSize = Array.from(this.cache.values()).reduce(
      (sum, entry) => sum + entry.size,
      0
    );

    if (currentSize + requiredSize <= this.maxSize) {
      return;
    }

    console.log(`⚠️  Cache full (${(currentSize / 1024 / 1024).toFixed(2)} MB), evicting...`);

    // Sort by least recently used (hits and recency)
    const sorted = Array.from(this.cache.entries()).sort((a, b) => {
      const scoreA = a[1].hits / (Date.now() - a[1].createdAt);
      const scoreB = b[1].hits / (Date.now() - b[1].createdAt);
      return scoreA - scoreB;
    });

    // Evict until we have space
    let freed = 0;
    for (const [key, entry] of sorted) {
      this.cache.delete(key);
      freed += entry.size;
      this.stats.evictions++;

      try {
        await blink.db.searchIndex.delete(entry.id);
      } catch (error) {
        console.warn('Failed to delete evicted cache from DB:', error);
      }

      if (currentSize - freed + requiredSize <= this.maxSize) {
        break;
      }
    }

    console.log(`✅ Evicted ${this.stats.evictions} entries, freed ${(freed / 1024).toFixed(2)} KB`);
  }

  /**
   * Match key against pattern (supports wildcards)
   */
  private matchesPattern(key: string, pattern: string): boolean {
    const regexPattern = pattern.replace(/\*/g, '.*').replace(/\?/g, '.');
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(key);
  }

  /**
   * Generate cache key from agent configuration
   */
  static generateKey(
    agentType: string,
    target: any,
    parameters?: Record<string, any>
  ): string {
    const normalizedParams = parameters 
      ? Object.keys(parameters)
          .sort()
          .map(k => `${k}=${JSON.stringify(parameters[k])}`)
          .join('&')
      : '';
    
    const targetStr = typeof target === 'string' ? target : JSON.stringify(target);
    return `${agentType}:${targetStr}:${normalizedParams}`;
  }

  /**
   * Load cache from database on startup
   */
  async loadFromDatabase(): Promise<void> {
    try {
      const entries = await blink.db.searchIndex.list({
        where: { itemType: 'agent_cache', userId: 'system' }
      });

      let loaded = 0;
      for (const dbEntry of entries) {
        const metadata = safeJsonParse(dbEntry.metadata || '{}');
        
        // Skip expired entries
        if (Date.now() > metadata.expiresAt) {
          await blink.db.searchIndex.delete(dbEntry.id);
          continue;
        }

        const cacheEntry: CacheEntry = {
          id: dbEntry.id,
          key: dbEntry.itemId,
          value: safeJsonParse(dbEntry.content || '{}'),
          createdAt: new Date(dbEntry.updatedAt || '').getTime(),
          expiresAt: metadata.expiresAt,
          hits: 0,
          size: metadata.size || 0,
          tags: metadata.tags || []
        };

        this.cache.set(cacheEntry.key, cacheEntry);
        loaded++;
      }

      console.log(`📂 Loaded ${loaded} cache entries from database`);
    } catch (error) {
      console.warn('Failed to load cache from database:', error);
    }
  }
}

/**
 * Singleton instance
 */
export const agentCacheManager = new AgentCacheManager();

/**
 * Initialize cache on module load
 */
agentCacheManager.loadFromDatabase().catch(console.error);
